import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, User, Mail, Phone, MapPin, Calendar, ShoppingCart, FileImage, DollarSign } from 'lucide-react';

import { API_URL } from '../../utils/api';

export default function AdminCustomerDetails() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/customers/${customerId}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 p-8 flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 p-8">
          <p className="text-ink/60">Customer not found</p>
        </div>
      </div>
    );
  }

  const { customer, orders, invitations, stats } = data;

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Button variant="ghost" onClick={() => navigate('/admin/customers')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>

          {/* Customer Info */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-ink/40" />
                    <div>
                      <p className="text-sm text-ink/60">Name</p>
                      <p className="font-medium">{customer.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-ink/40" />
                    <div>
                      <p className="text-sm text-ink/60">Email</p>
                      <p className="font-medium">{customer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-ink/40" />
                    <div>
                      <p className="text-sm text-ink/60">Phone</p>
                      <p className="font-medium">{customer.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-ink/40" />
                    <div>
                      <p className="text-sm text-ink/60">Joined</p>
                      <p className="font-medium">{new Date(customer.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                {customer.address && (
                  <div className="flex items-start gap-3 pt-4 border-t border-border">
                    <MapPin className="h-5 w-5 text-ink/40" />
                    <div>
                      <p className="text-sm text-ink/60">Address</p>
                      <p className="font-medium">{customer.address}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="space-y-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-6 w-6 text-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-ink/60">Total Spent</p>
                      <p className="text-2xl font-bold text-gold">${stats.total_spent.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-ink/60">Total Orders</p>
                      <p className="text-2xl font-bold text-blue-600">{stats.total_orders}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FileImage className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-ink/60">Invitations</p>
                      <p className="text-2xl font-bold text-purple-600">{stats.total_invitations}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Orders */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <p className="text-ink/60 text-center py-8">No orders yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-mono text-sm">
                          {order.order_numbers?.length ? order.order_numbers.join(', ') : `${order.id.slice(0, 8)}...`}
                        </TableCell>
                        <TableCell>{order.items?.length || 0} item(s)</TableCell>
                        <TableCell className="font-medium">${order.total_amount?.toFixed(2)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'paid' ? 'bg-green-100 text-green-700' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-ink/60">{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Link to={`/admin/orders/${order.id}`}>
                            <Button size="sm" variant="ghost">View</Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Invitations */}
          <Card>
            <CardHeader>
              <CardTitle>Invitations</CardTitle>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <p className="text-ink/60 text-center py-8">No invitations yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Share Link</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell className="font-medium">{invitation.name}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            invitation.is_paid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {invitation.is_paid ? 'Paid' : 'Unpaid'}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-ink/60">{invitation.share_link}</TableCell>
                        <TableCell className="text-ink/60">{new Date(invitation.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
